<template>
    <!-- only supporting one level(non nested) object for now -->
    <div class="BasicFilterInput">
        <PopoverEntityNumberFilter
            v-if="['number', 'integer'].includes(type)"
            class="BasicFilterInput__numberField "
            data-test-id="BasicFilterInput__numberField"
            :value="value"
            :type="type"
            :label="label"
            :placeholder="placeholder"
            @input="inputFilter"
            @scroll-to="emitScrollDistance"
        />
        <FormElement
            v-else-if="type === 'boolean'"
            :ref="`booleanDropdown-${label}`"
            class="BasicFilterInput__booleanField"
            data-test-id="BasicFilterInput__booleanField"
            :label="label"
        >
            <FormInput>
                <Dropdown
                    :options="booleanOptions"
                    :selected="convertBoolFilterToOptions(value)"
                    @input="onBooleanFieldChange"
                    @open="scrollToElement(`booleanDropdown-${label}`)"
                />
            </FormInput>
        </FormElement>
        <EnumFilter
            v-else-if="isEnumType || isArrayEnum"
            :ref="`EnumDropdown-${label}`"
            class="BasicFilterInput__enumField"
            :value="value"
            :schema="schema"
            :label="label"
            @input="inputFilter"
            @open="scrollToElement(`EnumDropdown-${label}`)"
        />
        <FormElement
            v-else
            class="BasicFilterInput__textField"
            data-test-id="BasicFilterInput__textField"
            :label="label"
        >
            <FormInput>
                <input
                    :value="value"
                    :type="type"
                    :placeholder="placeholder"
                    @input="inputEventFilter"
                >
            </FormInput>
        </FormElement>
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { Dropdown } from '@evidentid/dashboard-commons/components/Dropdown';
    import { DropdownOption } from '@evidentid/dashboard-commons/components/Dropdown/types';
    import { FormElement, FormInput } from '@evidentid/dashboard-commons/components/Form';
    import JsonSchema from '@evidentid/json-schema/interfaces/JsonSchema';
    import { isArray, isEnum } from '@evidentid/json-schema/schemaChecks';

    import PopoverEntityNumberFilter
        from '@/modules/entity-filtering/components/EntityFiltersPopover/PopoverEntityNumberFilter.vue';
    import EnumFilter from '@/modules/entity-filtering/components/EnumFilter/EnumFilter.vue';

    export default defineComponent({
        name: 'BasicFilterInput',
        components: {
            EnumFilter,
            Dropdown,
            FormElement,
            FormInput,
            PopoverEntityNumberFilter,
        },
        props: {
            type: {
                type: String as PropType<'text' | 'number' | 'integer' | 'boolean'>,
                default: 'text',
            },
            label: {
                type: String as PropType<string>,
                default: '',
            },
            placeholder: {
                type: String as PropType<string>,
                default: '',
            },
            value: {
                type: [ String, Number, Object, Array ] as PropType<
                    string | number | object | string[] | null | undefined
                >,
                default: undefined,
            },
            schema: {
                type: Object as PropType<JsonSchema>,
                default: undefined,
            },
        },
        emits: [ 'input', 'scroll-to' ],
        data() {
            return {
                booleanOptions: [
                    { label: 'Yes', value: 'true' },
                    { label: 'No', value: 'false' },
                ] as DropdownOption[],
            };
        },
        computed: {
            isEnumType(): boolean {
                return Boolean(this.schema && isEnum(this.schema));
            },
            isArrayEnum(): boolean {
                return Boolean(this.schema && isArray(this.schema) && isEnum(this.schema.items as JsonSchema));
            },
        },
        methods: {
            emitScrollDistance(distance: number): void {
                this.$emit('scroll-to', distance);
            },
            inputFilter(value: string | string[] | null): void {
                this.$emit('input', value);
            },
            inputEventFilter(inputEvent: InputEvent): void {
                this.inputFilter((inputEvent.target as HTMLInputElement).value);
            },
            onBooleanFieldChange(option: DropdownOption): void {
                this.inputFilter(option.value);
            },
            convertBoolFilterToOptions(value: 'true' | 'false'): DropdownOption[] {
                return value ? [ { label: value === 'true' ? 'Yes' : 'No', value } ] : [];
            },
            scrollToElement(refName: string): void {
                // refs returns array when it's within v-for
                const formElementList = this.$refs[refName] as FormElement;
                if (formElementList) {
                    this.$nextTick(() => {
                        this.emitScrollDistance((formElementList.$el as HTMLElement).offsetTop);
                    });
                }
            },
        },
    });
</script>
